/** Normalize a STAC datetime value to `YYYY-MM-DDTHH:mm:00Z` (UTC). */
export function normalizeStacUtcDateTime(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) {
    throw new Error('Date and time value is required.');
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return `${trimmed}T00:00:00Z`;
  }

  const isoMatch = trimmed.match(
    /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})?$/,
  );
  if (isoMatch) {
    const [, datePart, hours, minutes] = isoMatch;
    return `${datePart}T${hours}:${minutes}:00Z`;
  }

  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) {
    throw new Error('Date and time must be in a valid ISO format.');
  }

  const date = new Date(parsed);
  return `${toIsoDate(date)}T${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}:00Z`;
}

/** Parse a STAC datetime string into a UTC `Date`, or null when invalid. */
export function parseStacDateTime(value: string): Date | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    const normalized = normalizeStacUtcDateTime(trimmed);
    const parsed = Date.parse(normalized);
    return Number.isNaN(parsed) ? null : new Date(parsed);
  } catch {
    return null;
  }
}

/** Format a UTC datetime for UI display. */
export function formatStacDisplayDateTime(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
  const hours = pad2(date.getUTCHours());
  const minutes = pad2(date.getUTCMinutes());
  return `${day}-${month}-${date.getUTCFullYear()} ${hours}:${minutes}`;
}

/** Extract `HH:mm` for `<input type="time">` from a STAC datetime value. */
export function stacTimeInputValue(value: string): string {
  const parsed = parseStacDateTime(value);
  if (!parsed) return '00:00';
  return `${pad2(parsed.getUTCHours())}:${pad2(parsed.getUTCMinutes())}`;
}

/** Combine a date-only ISO string and `HH:mm` time into STAC UTC ISO. */
export function combineDateAndTimeUtc(datePart: string, timePart: string): string {
  const date = datePart.trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
    throw new Error('Date must be in YYYY-MM-DD format.');
  }
  const time = timePart.trim() || '00:00';
  const match = time.match(/^(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error('Time must be in HH:mm format.');
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    throw new Error('Time must be a valid 24-hour value.');
  }
  return `${date}T${match[1]}:${match[2]}:00Z`;
}

/** Inclusive UTC range check for item datetime against collection bounds. */
export function isDateTimeWithinRange(
  itemIso: string,
  startIso: string,
  endIso: string,
): boolean {
  const item = parseStacDateTime(itemIso);
  if (!item) return false;

  const start = startIso.trim() ? parseStacDateTime(startIso) : null;
  const end = endIso.trim() ? parseStacDateTime(endIso) : null;

  if (!start && !end) return true;
  const itemTime = item.getTime();
  if (start && itemTime < start.getTime()) return false;
  if (end && itemTime > end.getTime()) return false;
  return true;
}

/** Compare two STAC datetimes by UTC timestamp. */
export function compareStacDateTimes(left: string, right: string): number {
  const leftDate = parseStacDateTime(left);
  const rightDate = parseStacDateTime(right);
  if (!leftDate || !rightDate) return 0;
  return leftDate.getTime() - rightDate.getTime();
}

export interface CollectionBaseline {
  description: string;
  startDate: string;
  endDate: string;
  bbox: [number, number, number, number] | null;
}

/** Compare current collection state to a baseline snapshot. */
export function hasCollectionBaselineChanges(
  current: CollectionBaseline,
  baseline: CollectionBaseline | null,
): boolean {
  if (!baseline) return false;
  if (current.description.trim() !== baseline.description.trim()) return true;
  if (normalizeForCompare(current.startDate) !== normalizeForCompare(baseline.startDate)) {
    return true;
  }
  if (normalizeForCompare(current.endDate) !== normalizeForCompare(baseline.endDate)) {
    return true;
  }
  return !bboxEquals(current.bbox, baseline.bbox);
}

export interface ItemBaseline {
  datetime: string;
  bbox: [number, number, number, number] | null;
  geometryJson: string;
  propertiesJson: string;
}

/** Stable JSON for comparing item property rows. */
export function serializeItemProperties(properties: Record<string, unknown>): string {
  const sorted = Object.keys(properties).sort();
  const normalized: Record<string, unknown> = {};
  for (const key of sorted) {
    normalized[key] = properties[key];
  }
  return JSON.stringify(normalized);
}

/** Compare current item state to a baseline snapshot. */
export function hasItemBaselineChanges(
  current: ItemBaseline,
  baseline: ItemBaseline | null,
): boolean {
  if (!baseline) return false;
  if (normalizeForCompare(current.datetime) !== normalizeForCompare(baseline.datetime)) {
    return true;
  }
  if (!bboxEquals(current.bbox, baseline.bbox)) return true;
  if (current.geometryJson !== baseline.geometryJson) return true;
  return current.propertiesJson !== baseline.propertiesJson;
}

function normalizeForCompare(value: string): string {
  const parsed = parseStacDateTime(value);
  return parsed ? normalizeStacUtcDateTime(value) : value.trim();
}

function bboxEquals(
  left: [number, number, number, number] | null,
  right: [number, number, number, number] | null,
): boolean {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.every((value, index) => Math.abs(value - right[index]!) < 1e-8);
}

function toIsoDate(date: Date): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}
